import { useQuery } from "./useApi.js";
import { awards } from "../api/awards.service.js";
import type { AwardsQueryParams } from "../api/awards.types.js";

/**
 * Collapses one filter to a single primitive for the dependency list below. The
 * contract allows an array per filter (`status=winner,shortlisted`), and an
 * array is a new identity on every render, so it has to be flattened to the very
 * string `awards.service.ts` puts on the wire — two dep values then differ
 * exactly when two requests would.
 */
function toDep<T extends string | number>(value: T | T[] | undefined): string | T | undefined {
  return Array.isArray(value) ? value.join(",") : value;
}

/**
 * Staff awards list query.
 *
 * The dependency list must hold **primitives only**. `useQuery` hands it straight
 * to `useEffect`, which compares with `Object.is`, so passing the `filters` object
 * (or one of its arrays) would produce a new identity on every render and refetch
 * forever. Every filter `AwardsQueryParams` carries is listed here, so adding a
 * param to the type without adding it below is the bug to look for when a filter
 * change does not refetch.
 */
export function useAwards(filters?: AwardsQueryParams) {
  return useQuery(
    () => awards.getAll(filters),
    [
      filters?.limit,
      filters?.offset,
      toDep(filters?.product),
      toDep(filters?.awardType),
      toDep(filters?.series),
      toDep(filters?.status),
      toDep(filters?.shortlistTag),
      toDep(filters?.state),
      toDep(filters?.year),
      toDep(filters?.countryId),
      toDep(filters?.companyId),
      toDep(filters?.contactId),
      filters?.incomplete,
    ],
  );
}

/**
 * The full dictionaries behind the create/edit form's comboboxes. One request
 * per mount and no dependencies: the dictionaries change only when a form like
 * this one adds to them, and the page that does so navigates away afterwards.
 */
export function useAwardDictionaries() {
  return useQuery(() => awards.getDictionaries(), []);
}

/**
 * One award, for the edit form. Keyed on the id alone — the primitive that
 * changes exactly when the route does.
 */
export function useAward(id: string) {
  return useQuery(() => awards.getById(id), [id]);
}
